import React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import cx from 'clsx'
import LayoutContainer from 'components/Common/LayoutContainer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { NavWrapper } from 'components/OfferPage/styles'
import { OFFER_TYPE_HOTEL_SLUG } from 'constants/offer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import {
  OfferPackageOptionView,
  getCheapestSelectedPackageOption,
  getCheapestAvailablePackageOption,
  arePricesLoading,
  getFromDurationLabel,
  isAvailableForPurchase,
} from 'selectors/offerPage/offerPageSelectors'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import WalledContent from 'components/Common/WalledContent'
import TextButton from 'LuxKit/Button/TextButton'

import BodyText from 'LuxKit/Typography/BodyText'
import config from 'constants/config'
import GenericLoginSignupWall from 'components/Common/WalledContent/GenericLoginSignupWall'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import Group from 'components/utils/Group'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'

const Container = styled(NavWrapper)`
  &.hideOnMobile.hideOnMobile {
    display: none;
    ${mediaQueryUp.tablet} {
      display: flex;
      visibility: visible;
    }
  }
`

const Root = styled(LayoutContainer)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;

  ${mediaQueryUp.tablet} {
    margin-left: auto;
    justify-content: flex-end;
  }

  > * + * {
    margin-left: ${rem(12)};
  }
`

const MobileNewLine = styled.br`
  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const PriceWrapper = styled.div`
  display: flex;
  margin-left: auto;
  justify-content: flex-end;
  position: relative;

  & > * + * {
    margin-left: ${rem(8)};
  }
`
const InlineWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  & > * + * {
    margin-left: ${rem(8)};
  }

  ${mediaQueryUp.tablet} {
    justify-content: flex-end;
  }
`

interface Props {
  offer: App.Offer;
  onViewPackageOptions: () => void;
  cheapestSelectedOption?: OfferPackageOptionView;
  cheapestOption: OfferPackageOptionView;
  calendarsLoading?: boolean;
  durationLabel: string;
  availableForPurchase?: boolean;
}

function AlwaysOnStickyHeader(props: Props) {
  const {
    offer,
    onViewPackageOptions,
    cheapestOption,
    cheapestSelectedOption,
    calendarsLoading,
    durationLabel,
    availableForPurchase,
  } = props

  const dispatch = useOfferPageDispatch()

  const onViewOptionsClick = () => {
    dispatch({ type: OfferPageActions.closeFilter })
    onViewPackageOptions()
  }
  const displaySoldOutLabel = !availableForPurchase && !config.LERE_EXPIRED_DEALS_V2_ENABLED
  const hideSoldOutOnMobile = !availableForPurchase && config.LERE_EXPIRED_DEALS_V2_ENABLED

  const packageView = cheapestSelectedOption ?? cheapestOption

  const {
    pricing: hotelPricing,
  } = packageView

  return (
    <Container className={cx({ hideOnMobile: hideSoldOutOnMobile })}>
      <Root>
        {availableForPurchase && (
          <WalledContent enforced={offer.walledGarden} wall={<GenericLoginSignupWall />}>
            <PriceWrapper>
              <Group direction="vertical" horizontalAlign="end">
                <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>
                <PriceRowPrice
                  data-testid="always-on-sticky-header-price"
                  size="S"
                  price={hotelPricing.price}
                  saleUnit={hotelPricing.saleUnit}
                />
                <LoadingIndicator visible={calendarsLoading} floating opaque />
              </Group>
              <TextButton kind="primary" data-testid="offer-view-options" onClick={onViewOptionsClick}>
                <CSSBreakpoint max="tablet" as="span">
                  View options
                </CSSBreakpoint>
                <CSSBreakpoint min="desktop" as="span">
                  View package options
                </CSSBreakpoint>
              </TextButton>
            </PriceWrapper>
          </WalledContent>
        )}
        {!availableForPurchase && displaySoldOutLabel &&
          <InlineWrapper>
            <BodyText variant="medium" weight="bold" colour="neutral-one">
              This offer has<MobileNewLine /> SOLD OUT
            </BodyText>
            <TextButton kind="primary" to={`/${OFFER_TYPE_HOTEL_SLUG}`}>
              Browse all hotel offers
            </TextButton>
          </InlineWrapper>}
      </Root>
    </Container>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  cheapestSelectedOption: getCheapestSelectedPackageOption(appState, state),
  cheapestOption: getCheapestAvailablePackageOption(state, appState),
  calendarsLoading: arePricesLoading(appState, state),
  durationLabel: getFromDurationLabel(state, appState),
  availableForPurchase: isAvailableForPurchase(state, appState),
  open: !!state.activeFilter,
}), AlwaysOnStickyHeader, preventRenderWithOpenFilter)
